import axios from "axios";
import route from "ziggy-js";
import { computed, isRef, watch } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import { toast } from "@/SharedComponents/Toaster";
import { useChecklistStore } from "@/Stores/checklist";
import { useI18n } from "vue-i18n";

export function useTaskChecklist(task = null) {
    const _task = computed(() => (isRef(task) ? task.value : task));
    const taskId = computed(() => _task.value?.id);

    const user = computed(() => usePage().props.auth?.user);
    const _user = computed(() => ({
        id: user.value.id,
        fullname: user.value.fullname,
        avatar_url: user.value.avatar_url,
        avatar_initials: user.value.avatar_initials,
    }));

    const checklistStore = useChecklistStore();
    const { t } = useI18n();

    if (taskId.value && !checklistStore.getChecklist(taskId.value)) {
        checklistStore.setChecklist(taskId.value, _task.value.checklist);
    }

    const checklist = computed(() => {
        if (!taskId.value) return null;
        return checklistStore.getChecklist(taskId.value);
    });

    const busy = computed(() => checklistStore.state.isUpdating);

    watch(busy, () => {
        if (!busy.value) {
            router.reload();
        }
    });

    function updateChecklistItems(
        originalItems,
        newItems = [],
        replace = false
    ) {
        if (!taskId.value) return;
        checklistStore.updateChecklistItems(
            taskId.value,
            originalItems,
            newItems,
            replace
        );
    }

    function updateItemInChecklist(itemId, updates) {
        if (!taskId.value) return;
        checklistStore.updateItemInChecklist(taskId.value, itemId, updates);
    }

    async function addChecklist({ items }) {
        if (!taskId.value) return;

        checklistStore.setLoading(true);
        checklistStore.setError(null);

        try {
            const res = await axios.post(
                route("api.tasks.checklist.create", _task.value),
                {
                    items,
                }
            );
            checklistStore.setChecklist(taskId.value, res.data);
            toast.success(t("toasts.checklists.create.success"));
            return res.data;
        } catch (error) {
            checklistStore.setError(error);
            if (error.response?.status === 422) {
                toast.error(
                    Object.values(error.response.data.errors).flat().join(", ")
                );
            } else {
                toast.error(
                    error.response?.data?.message ||
                        t("toasts.checklists.create.error")
                );
            }
        } finally {
            checklistStore.setLoading(false);
        }
    }

    async function addItems({ items }) {
        if (!taskId.value) return;

        checklistStore.setLoading(true);
        checklistStore.setError(null);

        const originalItems = [...(checklist.value?.items || [])];
        updateChecklistItems(originalItems, items);

        try {
            const res = await axios.post(
                route("api.checklists.items.create", checklist.value),
                {
                    items,
                }
            );
            updateChecklistItems(res.data);
            toast.success(t("toasts.checklists.items.create.success"));
        } catch (error) {
            checklistStore.setError(error);
            updateChecklistItems(originalItems);
            if (error.response?.status === 422) {
                toast.error(
                    Object.values(error.response.data.errors).flat().join(", ")
                );
            } else {
                toast.error(
                    error.response?.data?.message ||
                        t("toasts.checklists.items.create.error")
                );
            }
        } finally {
            checklistStore.setLoading(false);
        }
    }

    async function duplicateItem({ item }) {
        return addItems({
            items: [
                {
                    id: "duplicate_item",
                    description: item.description,
                    position: item.position + 1,
                },
            ],
        }).catch(() => {
            toast.error(t("toasts.checklists.items.duplicate.error"));
        });
    }

    async function checkItem({ item }) {
        if (!taskId.value) return;

        checklistStore.setLoading(true);
        checklistStore.setError(null);

        updateItemInChecklist(item.id, {
            checked: true,
            checked_by: _user.value ?? null,
        });

        try {
            const res = await axios.patch(
                route("api.checklists.items.check", item)
            );
            updateChecklistItems(res.data);
            toast.success(t("toasts.checklists.items.update.success"));
        } catch (error) {
            checklistStore.setError(error);
            updateItemInChecklist(item.id, { checked: false });
            toast.error(
                error.response?.data?.message ||
                    t("toasts.checklists.items.update.error")
            );
        } finally {
            checklistStore.setLoading(false);
        }
    }

    async function checkAllItems() {
        if (!taskId.value) return;

        checklistStore.setLoading(true);
        checklistStore.setError(null);

        const originalItems = [...(checklist.value?.items || [])];
        updateChecklistItems(
            originalItems,
            originalItems.map((item) => ({
                ...item,
                checked: true,
                checked_by: _user.value ?? null,
            })),
            true
        );

        try {
            const res = await axios.patch(
                route("api.checklists.check.all", checklist.value)
            );
            updateChecklistItems(res.data);
            toast.success(t("toasts.checklists.items.update.success"));
        } catch (error) {
            checklistStore.setError(error);
            updateChecklistItems(originalItems);
            toast.error(
                error.response?.data?.message ||
                    t("toasts.checklists.items.update.error")
            );
        } finally {
            checklistStore.setLoading(false);
        }
    }

    async function uncheckItem({ item }) {
        if (!taskId.value) return;

        checklistStore.setLoading(true);
        checklistStore.setError(null);

        updateItemInChecklist(item.id, { checked: false });

        try {
            const res = await axios.patch(
                route("api.checklists.items.uncheck", item)
            );
            updateChecklistItems(res.data);
            toast.success(t("toasts.checklists.items.update.success"));
        } catch (error) {
            checklistStore.setError(error);
            updateItemInChecklist(item.id, { checked: true });
            toast.error(
                error.response?.data?.message ||
                    t("toasts.checklists.items.update.error")
            );
        } finally {
            checklistStore.setLoading(false);
        }
    }

    async function updateItem({ item, data }) {
        if (!taskId.value) return;

        checklistStore.setLoading(true);
        checklistStore.setError(null);

        const originalItem = { ...item };
        updateItemInChecklist(item.id, data);

        try {
            const res = await axios.put(
                route("api.checklists.items.update", item),
                data
            );
            updateChecklistItems(res.data);
            toast.success(t("toasts.checklists.items.update.success"));
        } catch (error) {
            checklistStore.setError(error);
            updateItemInChecklist(item.id, originalItem);
            if (error.response?.status === 422) {
                toast.error(
                    Object.values(error.response.data.errors).flat().join(", ")
                );
            } else {
                toast.error(
                    error.response?.data?.message ||
                        t("toasts.checklists.items.update.error")
                );
            }
        } finally {
            checklistStore.setLoading(false);
        }
    }

    async function deleteItem({ item }) {
        if (!taskId.value) return;

        checklistStore.setLoading(true);
        checklistStore.setError(null);

        const originalItems = [...(checklist.value?.items || [])];
        updateChecklistItems(originalItems.filter((i) => i.id !== item.id));

        try {
            const res = await axios.delete(
                route("api.checklists.items.delete", item)
            );
            if (res.data.length === 0) {
                checklistStore.setChecklist(taskId.value, null);
            } else {
                updateChecklistItems(res.data);
            }
            toast.success(t("toasts.checklists.items.delete.success"));
        } catch (error) {
            checklistStore.setError(error);
            updateChecklistItems(originalItems);
            toast.error(
                error.response?.data?.message ||
                    t("toasts.checklists.items.delete.error")
            );
        } finally {
            checklistStore.setLoading(false);
        }
    }

    return {
        checklist,
        busy,
        lastError: computed(() => checklistStore.state.lastError),
        addChecklist,
        addItems,
        duplicateItem,
        checkItem,
        checkAllItems,
        uncheckItem,
        updateItem,
        deleteItem,
    };
}
